import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageHeaderContext } from "@/contexts/PageHeaderContext";
import { MobilePageNav } from "@/components/layout/MobilePageNav";
import { useIsMobile } from "@/hooks/use-mobile";
import { matchContext } from "./contexts";
import { SectionPicker } from "./SectionPicker";
import { useContextNav } from "./useContextNav";
import type { MatchedContext } from "./types";

interface TrailPart {
  label: string | null | undefined;
  /** Where the part links to; the last part never links (it is the page). */
  href?: string;
}

function Trail({ parts }: { parts: TrailPart[] }) {
  return (
    <nav
      aria-label="Location"
      className="flex items-center gap-1 min-w-0 text-sm"
      data-testid="location-trail"
    >
      {parts.map((part, index) => {
        const isLast = index === parts.length - 1;
        return (
          <Fragment key={index}>
            {index > 0 && (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}
            {!part.label ? (
              <Skeleton className="h-4 w-24" />
            ) : isLast ? (
              <span
                className="truncate font-medium text-foreground"
                aria-current="page"
              >
                {part.label}
              </span>
            ) : part.href ? (
              <Link
                to={part.href}
                className="truncate text-muted-foreground hover:text-foreground hover:underline"
              >
                {part.label}
              </Link>
            ) : (
              <span className="truncate text-muted-foreground">
                {part.label}
              </span>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}

function ContextLocation({
  matched,
  pathname,
  isMobile,
}: {
  matched: MatchedContext;
  pathname: string;
  isMobile: boolean;
}) {
  const { header, items, activeChain } = useContextNav(matched, pathname);
  const { crumbs } = usePageHeaderContext();
  const title = header.trailTitle ?? header.title;

  if (isMobile) {
    return (
      <SectionPicker
        variant="drawer"
        title={header.title}
        items={items}
        activeChain={activeChain}
      />
    );
  }
  return (
    <Trail
      parts={[
        { label: title, href: matched.def.base(matched.params) },
        ...activeChain.map((item) => ({ label: item.label, href: item.href })),
        ...crumbs.map((label) => ({ label })),
      ]}
    />
  );
}

/**
 * The header's location label (M2): the location trail on desktop, the
 * section picker on mobile (P2). Pages outside a context show their title.
 */
export function PageLocation() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { title } = usePageHeaderContext();
  const matched = matchContext(location.pathname);

  if (matched) {
    return (
      <ContextLocation
        key={matched.def.type}
        matched={matched}
        pathname={location.pathname}
        isMobile={isMobile}
      />
    );
  }
  if (isMobile) return <MobilePageNav />;
  if (!title) return null;
  return <Trail parts={[{ label: title }]} />;
}
